import React, { useState, useEffect } from 'react';
import styles from '../CanvassInfo.module.css';
import reportStyles from './ReportSection.module.css';
import cbStyles from './ComprehensiveBusinessReport.module.css';
import { Icon } from '../../layout/icons/Icons';
import { Button } from '../../layout/buttons';
import { postAdminSummary } from '../../../utils/reports';
import { useUserRoles } from '../../../utils/useUserRoles';
import ComprehensiveReportV2View from './ComprehensiveReportV2View';

// Helper function to parse markdown formatting (bold and italics)
const parseMarkdownFormatting = (text) => {
  // Handle bold text (**text**)
  let processedText = text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  
  // Handle italic text (*text*)
  processedText = processedText.replace(/\*(.*?)\*/g, '<em>$1</em>');
  
  return processedText;
};

const Stat = ({ label, value }) => (
  <div className={cbStyles.statRow}>
    <div className={reportStyles.listKey}>{label}:</div>
    <div className={reportStyles.listValue}>{value ?? '—'}</div>
  </div>
);

export default function ComprehensiveBusinessReport({ data, performance, canvassId, onAdminSummaryUpdated }) {
  if (!data || typeof data !== 'object') return null;

  // Modernized comprehensive report (schemaVersion: comprehensive_report.v2)
  if (data.schemaVersion === 'comprehensive_report.v2') {
    return <ComprehensiveReportV2View data={data} performance={performance} />;
  }

  // Get user roles to check admin access
  const { isAdmin } = useUserRoles();

  // Support both reportMeta and reportMetadata naming
  const meta = data.reportMetadata || data.reportMeta || {};
  const source = meta.source || meta.dataSummary || {};

  const [summaryText, setSummaryText] = useState('');
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [summaryError, setSummaryError] = useState('');
  const [localAdminSummary, setLocalAdminSummary] = useState(null);

  // Check if admin executive summary exists in data or local state
  const existingAdminSummary = localAdminSummary || data?.adminExecutiveSummary;

  // Initialize summary text if existing summary is present
  useEffect(() => {
    if (existingAdminSummary) {
      setSummaryText(existingAdminSummary);
    }
  }, [existingAdminSummary]);

  const formatValue = (value) => {
    if (value === null || value === undefined) return '—';
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return String(value);
    if (Array.isArray(value)) {
      const parts = value
        .map((v) => {
          if (v === null || v === undefined) return '';
          if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return String(v);
          if (typeof v === 'object') {
            if (typeof v.label === 'string') return v.label;
            if (typeof v.name === 'string') return v.name;
            if (typeof v.stage === 'string') return v.stage;
            if (typeof v.summary === 'string') return v.summary;
            if (typeof v.description === 'string') return v.description;
            return JSON.stringify(v);
          }
          return String(v);
        })
        .filter(Boolean);
      return parts.length > 0 ? parts.join(', ') : '—';
    }
    if (typeof value === 'object') {
      if (typeof value.summary === 'string') return value.summary;
      if (typeof value.description === 'string') return value.description;
      return JSON.stringify(value);
    }
    return String(value);
  };

  const renderPills = (values, keyPrefix) => {
    if (!Array.isArray(values) || values.length === 0) return null;
    return (
      <div className={cbStyles.emotionsRow}>
        {values
          .map((v) => formatValue(v))
          .filter((v) => v && v !== '—')
          .map((v, i) => (
            <span key={`${keyPrefix}-${i}`} className={reportStyles.pill}>{v}</span>
          ))}
      </div>
    );
  };

  const shouldRenderEmotionalRole = (emotionalRole) => {
    if (!emotionalRole) return false;
    const text = formatValue(emotionalRole);
    if (!text || text === '—') return false;
    return !String(text).includes('[object Object]');
  };

  const shouldRenderEmotionalImpact = (emotionalImpact) => {
    if (!emotionalImpact) return false;
    const text = formatValue(emotionalImpact);
    if (!text || text === '—') return false;
    return !String(text).includes('[object Object]');
  };

  const [collapsed, setCollapsed] = useState(true);
  const [thematicAreasCollapsed, setThematicAreasCollapsed] = useState(true);
  const [businessIntelligenceCollapsed, setBusinessIntelligenceCollapsed] = useState(true);
  const [coreEmotionalDriversCollapsed, setCoreEmotionalDriversCollapsed] = useState(true);
  const [operationalRisksCollapsed, setOperationalRisksCollapsed] = useState(true);
  const [opportunitiesCollapsed, setOpportunitiesCollapsed] = useState(true);
  const [satisfactionVolatilityCollapsed, setSatisfactionVolatilityCollapsed] = useState(true);
  const [satisfactionAndCoveragePatternsCollapsed, setSatisfactionAndCoveragePatternsCollapsed] = useState(true);
  const [coreDriversCollapsed, setCoreDriversCollapsed] = useState(true);
  const [growthOpportunitiesCollapsed, setGrowthOpportunitiesCollapsed] = useState(true);

  return (
    <section>
      <div className={`${styles.sectionHeader} ${cbStyles.sectionHeaderPad}`}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div className={styles.sectionTitle}>Comprehensive</div>
          <button
            type="button"
            aria-label={collapsed ? 'Expand' : 'Collapse'}
            onClick={() => setCollapsed(v => !v)}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              fontSize: 16,
              lineHeight: 1,
            }}
          >
            {collapsed ? (
              <Icon
                name="arrows/arrow_right"
                width={28}
                height={28}
                color="var(--system-sky-600)"
              />
            ) : (
              <Icon
                name="arrows/arrow_drop_down"
                width={28}
                height={28}
                color="var(--system-sky-600)"
              />
            )}
          </button>
        </div>
        {!collapsed && (
          <div className={styles.sectionSubTitle + ' ' + reportStyles.textSecondary}>
            Synthesized, emotion-first business intelligence for this canvass
          </div>
        )}
      </div>

      {!collapsed && (
        <>
          {/* Executive Summary Section */}
          {canvassId && (existingAdminSummary || loadingSummary || summaryError) && (
            <div className={styles.section}>
              <div className={`${styles.sectionSubTitle} ${cbStyles.sectionMainHeader} ${cbStyles.sectionHeaderPadMd}`}>
                Executive Summary
                {loadingSummary && <span style={{ marginLeft: 8, fontSize: 12, opacity: 0.7 }}>Loading...</span>}
              </div>
              
              {summaryError && (
                <div className={`${reportStyles.textSecondary} ${cbStyles.sectionHeaderPadMd}`} style={{ color: 'var(--system-red-600)' }}>
                  {summaryError}
                </div>
              )}
              
              {existingAdminSummary && (
                <div className={`${cbStyles.sectionHeaderPadMd}`}>
                  <div className={`${reportStyles.textLeft} ${reportStyles.mb8}`}>
                    {existingAdminSummary.split('\n\n').map((paragraph, index) => (
                      <div 
                        key={index} 
                        style={{ marginBottom: '1em', lineHeight: '1.5', display: 'block' }}
                        dangerouslySetInnerHTML={{
                          __html: paragraph.split('\n').map((line, lineIndex) => {
                            const processedLine = parseMarkdownFormatting(line);
                            return lineIndex > 0 ? `<br>${processedLine}` : processedLine;
                          }).join('')
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {!existingAdminSummary && !isAdmin && (
                <div className={`${cbStyles.sectionHeaderPadMd}`}>
                  <div className={`${reportStyles.textSecondary}`}>
                    No executive summary available.
                  </div>
                </div>
              )}
            </div>
          )}

          {data?.objectiveAnswer && (
            <div className={styles.section}>
              <div className={`${styles.sectionSubTitle} ${cbStyles.sectionMainHeader} ${cbStyles.sectionHeaderPadMd}`}>
                Objective Answer
              </div>

              {data.objectiveAnswer.conclusion && (
                <div className={`${reportStyles.textLeft} ${reportStyles.mb8} ${cbStyles.sectionHeaderPadMd}`}>
                  {data.objectiveAnswer.conclusion}
                </div>
              )}

              {Array.isArray(data.objectiveAnswer.prioritizedPolicyAreas) && data.objectiveAnswer.prioritizedPolicyAreas.length > 0 && (
                <div className={reportStyles.mb8}>
                  <div className={`${styles.sectionSubTitle} ${cbStyles.sectionSubHeader} ${cbStyles.sectionHeaderPadMd}`}>
                    Prioritized Policy Areas
                  </div>
                  <div className={cbStyles.emotionalBeatsGrid}>
                    {data.objectiveAnswer.prioritizedPolicyAreas.map((area, i) => (
                      <div key={i} className={`${reportStyles.textLeft} ${cbStyles.beatCard}`}>
                        <div className={cbStyles.beatStage}>
                          {area?.policyArea || '—'}
                          {typeof area?.rank === 'number' && (
                            <span className={reportStyles.pill} style={{ marginLeft: 8 }}>Rank {area.rank}</span>
                          )}
                        </div>
                        {area?.problemStatement && (
                          <div className={`${reportStyles.textSecondary} ${cbStyles.beatDescription}`}>
                            <strong>Problem:</strong> {area.problemStatement}
                          </div>
                        )}
                        {area?.evidence && (
                          <div className={`${reportStyles.textSecondary} ${cbStyles.beatDescription}`}>
                            <strong>Evidence:</strong> {area.evidence}
                          </div>
                        )}
                        {area?.recommendedChange && (
                          <div className={`${reportStyles.textSecondary} ${cbStyles.beatDescription}`}>
                            <strong>Recommended Change:</strong> {area.recommendedChange}
                          </div>
                        )}
                        {area?.confidence && (
                          <div className={reportStyles.mb8}>
                            <span className={reportStyles.pill}>{String(area.confidence)}</span>
                          </div>
                        )}
                        {area?.caveat && (
                          <div className={`${reportStyles.textSecondary} ${cbStyles.beatDescription}`}>
                            <strong>Caveat:</strong> {area.caveat}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {Array.isArray(data.objectiveAnswer.secondaryAreas) && data.objectiveAnswer.secondaryAreas.length > 0 && (
                <div className={reportStyles.mb8}>
                  <div className={`${styles.sectionSubTitle} ${cbStyles.sectionSubHeader} ${cbStyles.sectionHeaderPadMd}`}>
                    Secondary Areas
                  </div>
                  <div className={cbStyles.emotionalBeatsGrid}>
                    {data.objectiveAnswer.secondaryAreas.map((area, i) => (
                      <div key={i} className={`${reportStyles.textLeft} ${cbStyles.beatCard}`}>
                        <div className={cbStyles.beatStage}>{area?.topic || '—'}</div>
                        {area?.note && (
                          <div className={`${reportStyles.textSecondary} ${cbStyles.beatDescription}`}>
                            {area.note}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {data.objectiveAnswer.evidenceCaveat && (
                <div className={`${reportStyles.textSecondary} ${cbStyles.sectionHeaderPadMd}`}>
                  <strong>Evidence Caveat:</strong> {data.objectiveAnswer.evidenceCaveat}
                </div>
              )}
            </div>
          )}

      {data.emotionalArc && (
        <div className={styles.section}>
          <div className={`${styles.sectionSubTitle} ${cbStyles.sectionMainHeader} ${cbStyles.sectionHeaderPadMd}`}>Emotional Narratives</div>
          <div className={`${reportStyles.textSecondary} ${reportStyles.mb8} ${cbStyles.sectionHeaderPadSm}`}>
            {data.emotionalArc.dominantPattern || '—'}
          </div>
          <div className={`${reportStyles.textLeft} ${reportStyles.mb8} ${cbStyles.sectionHeaderPadMd}`}>
            {data.emotionalArc.arcDescription || '—'}
          </div>

          {Array.isArray(data.emotionalArc.emotionalBeats) && data.emotionalArc.emotionalBeats.length > 0 && (
            <div className={reportStyles.mb8}>
              <div className={`${styles.sectionSubTitle} ${cbStyles.sectionSubHeader} ${cbStyles.sectionHeaderPadMd}`}>Key Emotional Moments</div>
              <div className={`${cbStyles.emotionalBeatsGrid} ${cbStyles.sectionHeaderPadMd}`}>
                {data.emotionalArc.emotionalBeats.map((beat, idx) => (
                  <div key={idx} className={`${reportStyles.textLeft} ${cbStyles.beatCard}`}>
                    <div className={cbStyles.beatStage}>{beat?.stage || beat?.summary || '—'}</div>
                    <div className={`${reportStyles.textSecondary} ${cbStyles.beatDescription}`}>{beat?.description || '—'}</div>
                    {Array.isArray(beat?.primaryEmotions) && beat.primaryEmotions.length > 0 && (
                      <div className={cbStyles.emotionsRow}>
                        {beat.primaryEmotions.map((e, i) => (
                          <span key={i} className={reportStyles.pill}>{String(e)}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {Array.isArray(data.emotionalArc.emotionalRisks) && data.emotionalArc.emotionalRisks.length > 0 && (
            <div className={reportStyles.mb8}>
              <div className={`${styles.sectionSubTitle} ${cbStyles.sectionSubHeader} ${cbStyles.sectionHeaderPadMd}`}>Emotional Risks</div>
              <div className={cbStyles.emotionsRow}>
                {data.emotionalArc.emotionalRisks.map((r, i) => (
                  <span key={i} className={reportStyles.pill}>{String(r)}</span>
                ))}
              </div>
            </div>
          )}

          {Array.isArray(data.emotionalArc.emotionalOpportunities) && data.emotionalArc.emotionalOpportunities.length > 0 && (
            <div>
              <div className={`${styles.sectionSubTitle} ${cbStyles.sectionSubHeader} ${cbStyles.sectionHeaderPadMd}`}>Emotional Opportunities</div>
              <div className={cbStyles.emotionsRow}>
                {data.emotionalArc.emotionalOpportunities.map((o, i) => (
                  <span key={i} className={reportStyles.pill}>{String(o)}</span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

            {/* Analyzed Interviews Section */}
      {!collapsed &&(meta.dataSummary || performance) && (
        <div className={styles.section}>
          <div className={`${styles.sectionSubTitle} ${cbStyles.sectionMainHeader} ${cbStyles.sectionHeaderPadMd}`}>Analyzed Interviews</div>
          
          <div className={`${cbStyles.statsGrid} ${cbStyles.sectionHeaderPadMd}`}>
            {/* Interview Processing Stats from dataSummary */}
            {meta.dataSummary?.interviewsProcessed !== undefined && (
              <Stat 
                label="Interviews Processed" 
                value={meta.dataSummary.interviewsProcessed.toLocaleString()} 
              />
            )}
            
            {/* Performance Stats */}
            {performance?.completedVoices !== undefined && (
              <Stat 
                label="Completed Voices" 
                value={performance.completedVoices.toLocaleString()} 
              />
            )}
            
            {performance?.abandonedVoices !== undefined && (
              <Stat 
                label="Abandoned Interviews" 
                value={performance.abandonedVoices.toLocaleString()} 
              />
            )}
            
            {/* Completion Rate - prefer performance data, fallback to dataSummary */}
            {(performance?.completionRate !== undefined || meta.dataSummary?.completionRate !== undefined) && (
              <Stat 
                label="Completion Rate" 
                value={`${(performance?.completionRate ?? meta.dataSummary?.completionRate ?? 0).toFixed(1)}%`} 
              />
            )}
          </div>
        </div>
      )}

      {!collapsed && data.thematicAreas && typeof data.thematicAreas === 'object' && (
        <div className={styles.section}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className={`${styles.sectionSubTitle} ${cbStyles.sectionMainHeader} ${cbStyles.sectionHeaderPadMd} ${cbStyles.sectionHeaderPadTopMd}`}>Thematic Areas</div>
            <button
              type="button"
              aria-label={thematicAreasCollapsed ? 'Expand' : 'Collapse'}
              onClick={() => setThematicAreasCollapsed(v => !v)}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 16, lineHeight: 1 }}
            >
              {thematicAreasCollapsed ? (
                <Icon
                  name="arrows/arrow_right"
                  width={28}
                  height={28}
                  color="var(--system-sky-600)"
                />
              ) : (
                <Icon
                  name="arrows/arrow_drop_down"
                  width={28}
                  height={28}
                  color="var(--system-sky-600)"
                />
              )}
            </button>
          </div>
          <div className={cbStyles.sectionHeaderPadSm}></div>

          {!thematicAreasCollapsed && (

          <div style={{ display: 'grid', gap: 16 }}>
            {Object.entries(data.thematicAreas).map(([title, area], idx) => (
              <div key={`${String(title)}-${idx}`} className={reportStyles.textLeft}>
                <div className={`${styles.sectionSubTitle} ${cbStyles.sectionSubHeader} ${cbStyles.sectionHeaderPadSm}`}>
                  {String(title).replace(/[_-]+/g, ' ')}
                </div>

                {area?.summary && (
                  <div className={`${reportStyles.mb8}`}>
                    <div className={`${reportStyles.textSecondary} ${cbStyles.sectionHeaderPadTopMd} ${cbStyles.sectionHeaderPadMd}`}>Summary</div>
                    <div>{String(area.summary)}</div>
                  </div>
                )}

                {shouldRenderEmotionalRole(area?.emotionalRole) && (
                  <div className={`${reportStyles.mb8}`}>
                    <div className={`${reportStyles.textSecondary} ${cbStyles.sectionHeaderPadTopMd} ${cbStyles.sectionHeaderPadMd}`}>Emotional Role</div>
                    <div>{formatValue(area.emotionalRole)}</div>
                  </div>
                )}

                {Array.isArray(area?.signalsAndInferences) && area.signalsAndInferences.length > 0 && (
                  <div className={reportStyles.mb8}>
                    <div className={`${reportStyles.textSecondary} ${cbStyles.sectionHeaderPadTopMd} ${cbStyles.sectionHeaderPadMd}`}>Signals & Inferences</div>
                    <div className={`${cbStyles.emotionsRow}`}>
                      {area.signalsAndInferences.map((s, i) => (
                        <span key={i} className={reportStyles.pill}>{String(s)}</span>
                      ))}
                    </div>
                  </div>
                )}

                {Array.isArray(area?.subThemes) && area.subThemes.length > 0 && (
                  <div className={reportStyles.mb8}>
                    <div className={`${reportStyles.textSecondary} ${cbStyles.sectionHeaderPadTopMd} ${cbStyles.sectionHeaderPadMd}`}>Sub-themes</div>
                    <div className={cbStyles.emotionalBeatsGrid}>
                      {area.subThemes
                        .filter((t) => {
                          const name = t?.name;
                          return typeof name === 'string' && name.trim().length > 0;
                        })
                        .map((t, i) => (
                        <div key={i} className={cbStyles.beatCard}>
                          <div className={cbStyles.beatStage}>{String(t?.name || '—').replace(/[_-]+/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</div>
                          {t?.description && <div className={`${reportStyles.textSecondary} ${cbStyles.beatDescription}`}>{t.description}</div>}
                          {Array.isArray(t?.evidence) && t.evidence.length > 0 && (
                            <div className={reportStyles.mb8}>
                              <div className={`${reportStyles.textSecondary} ${cbStyles.sectionHeaderPadSm}`}>Evidence</div>
                              <div className={`${cbStyles.emotionsRow}`}>
                                {t.evidence.map((ev, j) => (
                                  <span key={`${i}-${j}`} className={reportStyles.interviewQuote}>{String(ev)}</span>
                                ))}
                              </div>
                            </div>
                          )}
                          {t?.desiredAttributes && (
                            <div className={reportStyles.mb8}>
                              <div className={`${reportStyles.textSecondary} ${cbStyles.sectionHeaderPadSm}`}>Desired Attributes</div>
                              <div className={reportStyles.textLeft}>
                                {Object.entries(t.desiredAttributes).map(([key, value]) => (
                                  <div key={key} className={reportStyles.mb8}>
                                    <div className={`${reportStyles.listKey} ${cbStyles.sectionHeaderPadSm}`}>
                                      {(() => {
                                        const label = String(key);
                                        return label.charAt(0).toUpperCase() + label.slice(1);
                                      })()}
                                    </div>
                                    <div className={reportStyles.listValue}>
                                      {Array.isArray(value) ? (
                                        <div className={cbStyles.emotionsRow}>
                                          {value.map((v, idx) => (
                                            <span key={idx} className={reportStyles.pill}>{String(v)}</span>
                                          ))}
                                        </div>
                                      ) : (
                                        String(value)
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {shouldRenderEmotionalImpact(t?.emotionalImpact) && (
                            <div className={reportStyles.mb8}>
                              <div className={`${reportStyles.textSecondary} ${cbStyles.sectionHeaderPadTopMd} ${cbStyles.sectionHeaderPadMd}`}>Emotional Impact</div>
                              <div>{formatValue(t.emotionalImpact)}</div>
                            </div>
                          )}
                          {t?.businessSignal && (
                            <div className={reportStyles.mb8}>
                              <div className={`${reportStyles.textSecondary} ${cbStyles.sectionHeaderPadTopMd} ${cbStyles.sectionHeaderPadMd}`}>Business Signal</div>
                              <div>{String(t.businessSignal)}</div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          )}
        </div>
      )}

      {!collapsed && data.businessIntelligence && typeof data.businessIntelligence === 'object' && (
        <div className={styles.section}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className={`${styles.sectionSubTitle} ${cbStyles.sectionMainHeader} ${cbStyles.sectionHeaderPadMd} ${cbStyles.sectionHeaderPadTopMd}`}>
              Business Intelligence
            </div>
            <button
              type="button"
              aria-label={businessIntelligenceCollapsed ? 'Expand' : 'Collapse'}
              onClick={() => setBusinessIntelligenceCollapsed(v => !v)}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 16, lineHeight: 1 }}
            >
              {businessIntelligenceCollapsed ? (
                <Icon
                  name="arrows/arrow_right"
                  width={28}
                  height={28}
                  color="var(--system-sky-600)"
                />
              ) : (
                <Icon
                  name="arrows/arrow_drop_down"
                  width={28}
                  height={28}
                  color="var(--system-sky-600)"
                />
              )}
            </button>
          </div>
          <div className={cbStyles.sectionHeaderPadSm}></div>

          {!businessIntelligenceCollapsed && (
              <>
                {Array.isArray(data.businessIntelligence.coreEmotionalDrivers) && data.businessIntelligence.coreEmotionalDrivers.length > 0 && (
                  <div className={reportStyles.mb8}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div className={`${styles.sectionSubTitle} ${reportStyles.textLeft} ${cbStyles.sectionHeaderPad} ${cbStyles.sectionSubHeader}`}>
                        Core Emotional Drivers
                      </div>
                      <button
                        type="button"
                        aria-label={coreEmotionalDriversCollapsed ? 'Expand' : 'Collapse'}
                        onClick={() => setCoreEmotionalDriversCollapsed(v => !v)}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 16, lineHeight: 1, display: 'flex', alignItems: 'center' }}
                      >
                        {coreEmotionalDriversCollapsed ? (
                          <Icon
                            name="arrows/arrow_right"
                            width={28}
                            height={28}
                            color="var(--system-sky-600)"
                          />
                        ) : (
                          <Icon
                            name="arrows/arrow_drop_down"
                            width={28}
                            height={28}
                            color="var(--system-sky-600)"
                          />
                        )}
                      </button>
                    </div>
                    {!coreEmotionalDriversCollapsed && (
                      <>
                        {typeof data.businessIntelligence.coreEmotionalDrivers[0] === 'string' ? (
                          <div className={cbStyles.emotionalBeatsGrid}>
                            {data.businessIntelligence.coreEmotionalDrivers.map((driver, i) => (
                              <div key={i} className={`${reportStyles.textLeft} ${cbStyles.beatCard}`}>
                                <div className={cbStyles.beatStage}>{formatValue(driver)}</div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className={cbStyles.emotionalBeatsGrid}>
                            {data.businessIntelligence.coreEmotionalDrivers.map((driver, i) => (
                              <div key={i} className={`${reportStyles.textLeft} ${cbStyles.beatCard}`}>
                                <div className={cbStyles.beatStage}>{driver?.theme || driver?.summary || driver?.description || '—'}</div>
                                {driver?.description && (
                                  <div className={`${reportStyles.textSecondary} ${cbStyles.beatDescription}`}>
                                    {formatValue(driver.description)}
                                  </div>
                                )}
                                {driver?.pattern && (
                                  <div className={`${reportStyles.textSecondary} ${cbStyles.beatDescription}`}>
                                    {formatValue(driver.pattern)}
                                  </div>
                                )}
                                {Array.isArray(driver?.needs) && driver.needs.length > 0 && (
                                  <div className={reportStyles.mb8}>
                                    <div className={reportStyles.textSecondary}>Needs</div>
                                    {renderPills(driver.needs, `driver-needs-${i}`)}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {Array.isArray(data.businessIntelligence.operationalRisks) && data.businessIntelligence.operationalRisks.length > 0 && (
                  <div className={reportStyles.mb8}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div className={`${styles.sectionSubTitle} ${reportStyles.textLeft} ${cbStyles.sectionHeaderPad} ${cbStyles.sectionSubHeader} ${cbStyles.sectionHeaderPadTopMd}`}>Operational Risks</div>
                      <button
                        type="button"
                        aria-label={operationalRisksCollapsed ? 'Expand' : 'Collapse'}
                        onClick={() => setOperationalRisksCollapsed(v => !v)}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 16, lineHeight: 1, display: 'flex', alignItems: 'center' }}
                      >
                        {operationalRisksCollapsed ? (
                          <Icon
                            name="arrows/arrow_right"
                            width={28}
                            height={28}
                            color="var(--system-sky-600)"
                          />
                        ) : (
                          <Icon
                            name="arrows/arrow_drop_down"
                            width={28}
                            height={28}
                            color="var(--system-sky-600)"
                          />
                        )}
                      </button>
                    </div>
                    {!operationalRisksCollapsed && (
                      <>
                        {typeof data.businessIntelligence.operationalRisks[0] === 'string' ? (
                          <div className={cbStyles.emotionalBeatsGrid}>
                            {data.businessIntelligence.operationalRisks.map((risk, i) => (
                              <div key={i} className={`${reportStyles.textLeft} ${cbStyles.beatCard}`}>
                                <div className={cbStyles.beatStage}>{formatValue(risk)}</div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className={cbStyles.emotionalBeatsGrid}>
                            {data.businessIntelligence.operationalRisks.map((risk, i) => (
                              <div key={i} className={`${reportStyles.textLeft} ${cbStyles.beatCard}`}>
                                <div className={cbStyles.beatStage}>{risk?.area || risk?.summary || '—'}</div>
                                {risk?.description && (
                                  <div className={`${reportStyles.textSecondary} ${cbStyles.beatDescription}`}>
                                    {formatValue(risk.description)}
                                  </div>
                                )}
                                {risk?.risk && (
                                  <div className={`${reportStyles.textSecondary} ${cbStyles.beatDescription}`}>
                                    {formatValue(risk.risk)}
                                  </div>
                                )}
                                {Array.isArray(risk?.drivers) && risk.drivers.length > 0 && (
                                  <div className={reportStyles.mb8}>
                                    <div className={reportStyles.textSecondary}>Drivers</div>
                                    {renderPills(risk.drivers, `risk-drivers-${i}`)}
                                  </div>
                                )}
                                {risk?.emotionalImpact && (
                                  <div className={reportStyles.mb8}>
                                    <div className={reportStyles.textSecondary}>Emotional Impact</div>
                                    <div className={reportStyles.listValue}>{formatValue(risk.emotionalImpact)}</div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {Array.isArray(data.businessIntelligence.opportunities) && data.businessIntelligence.opportunities.length > 0 && (
                  <div className={reportStyles.mb8}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div className={`${styles.sectionSubTitle} ${reportStyles.textLeft} ${cbStyles.sectionHeaderPad} ${cbStyles.sectionSubHeader} ${cbStyles.sectionHeaderPadTopMd}`}>Oppourtunities</div>
                      <button
                        type="button"
                        aria-label={opportunitiesCollapsed ? 'Expand' : 'Collapse'}
                        onClick={() => setOpportunitiesCollapsed(v => !v)}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 16, lineHeight: 1, display: 'flex', alignItems: 'center' }}
                      >
                        {opportunitiesCollapsed ? (
                          <Icon
                            name="arrows/arrow_right"
                            width={28}
                            height={28}
                            color="var(--system-sky-600)"
                          />
                        ) : (
                          <Icon
                            name="arrows/arrow_drop_down"
                            width={28}
                            height={28}
                            color="var(--system-sky-600)"
                          />
                        )}
                      </button>
                    </div>
                    {!opportunitiesCollapsed && (
                      <>
                        {typeof data.businessIntelligence.opportunities[0] === 'string' ? (
                          <div className={cbStyles.emotionalBeatsGrid}>
                            {data.businessIntelligence.opportunities.map((opp, i) => (
                              <div key={i} className={`${reportStyles.textLeft} ${cbStyles.beatCard}`}>
                                <div className={cbStyles.beatStage}>{formatValue(opp)}</div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className={cbStyles.emotionalBeatsGrid}>
                            {data.businessIntelligence.opportunities.map((opp, i) => (
                              <div key={i} className={`${reportStyles.textLeft} ${cbStyles.beatCard}`}>
                                <div className={cbStyles.beatStage}>{opp?.area || opp?.summary || '—'}</div>
                                {opp?.description && (
                                  <div className={`${reportStyles.textSecondary} ${cbStyles.beatDescription}`}>
                                    {formatValue(opp.description)}
                                  </div>
                                )}
                                {opp?.play && (
                                  <div className={`${reportStyles.textSecondary} ${cbStyles.beatDescription}`}>
                                    {formatValue(opp.play)}
                                  </div>
                                )}
                                {Array.isArray(opp?.levers) && opp.levers.length > 0 && (
                                  <div className={reportStyles.mb8}>
                                    <div className={reportStyles.textSecondary}>Levers</div>
                                    {renderPills(opp.levers, `opp-levers-${i}`)}
                                  </div>
                                )}
                                {opp?.benefit && (
                                  <div className={reportStyles.mb8}>
                                    <div className={reportStyles.textSecondary}>Benefit</div>
                                    <div className={reportStyles.listValue}>{formatValue(opp.benefit)}</div>
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {data.businessIntelligence.satisfactionVolatility && (
                  <div className={reportStyles.mb8}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div className={`${styles.sectionSubTitle} ${reportStyles.textLeft} ${cbStyles.sectionHeaderPad} ${cbStyles.sectionSubHeader} ${cbStyles.sectionHeaderPadTopMd}`}>Satisfaction Volatility</div>
                      <button
                        type="button"
                        aria-label={satisfactionVolatilityCollapsed ? 'Expand' : 'Collapse'}
                        onClick={() => setSatisfactionVolatilityCollapsed(v => !v)}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 16, lineHeight: 1, display: 'flex', alignItems: 'center' }}
                      >
                        {satisfactionVolatilityCollapsed ? (
                          <Icon
                            name="arrows/arrow_right"
                            width={28}
                            height={28}
                            color="var(--system-sky-600)"
                          />
                        ) : (
                          <Icon
                            name="arrows/arrow_drop_down"
                            width={28}
                            height={28}
                            color="var(--system-sky-600)"
                          />
                        )}
                      </button>
                    </div>
                    {!satisfactionVolatilityCollapsed && (
                      <div className={cbStyles.emotionalBeatsGrid}>
                        {data.businessIntelligence.satisfactionVolatility.threshold && (
                          <div className={`${reportStyles.textLeft} ${cbStyles.beatCard}`}>
                            <div className={cbStyles.beatStage}>Threshold</div>
                            <div className={`${reportStyles.textSecondary} ${cbStyles.beatDescription}`}>
                              {formatValue(data.businessIntelligence.satisfactionVolatility.threshold)}
                            </div>
                          </div>
                        )}
                        {Array.isArray(data.businessIntelligence.satisfactionVolatility.recoveryRequirements) && data.businessIntelligence.satisfactionVolatility.recoveryRequirements.length > 0 && (
                          <div className={`${reportStyles.textLeft} ${cbStyles.beatCard}`}>
                            <div className={cbStyles.beatStage}>Recovery Requirements</div>
                            <div className={reportStyles.mb8}>
                              {renderPills(data.businessIntelligence.satisfactionVolatility.recoveryRequirements, 'recovery-requirements')}
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {Array.isArray(data.businessIntelligence.satisfactionAndCoveragePatterns) && data.businessIntelligence.satisfactionAndCoveragePatterns.length > 0 && (
                  <div className={reportStyles.mb8}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div className={`${styles.sectionSubTitle} ${reportStyles.textLeft} ${cbStyles.sectionHeaderPad} ${cbStyles.sectionSubHeader} ${cbStyles.sectionHeaderPadTopMd}`}>Satisfaction &amp; Coverage Patterns</div>
                      <button
                        type="button"
                        aria-label={satisfactionAndCoveragePatternsCollapsed ? 'Expand' : 'Collapse'}
                        onClick={() => setSatisfactionAndCoveragePatternsCollapsed(v => !v)}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 16, lineHeight: 1, display: 'flex', alignItems: 'center' }}
                      >
                        {satisfactionAndCoveragePatternsCollapsed ? (
                          <Icon
                            name="arrows/arrow_right"
                            width={28}
                            height={28}
                            color="var(--system-sky-600)"
                          />
                        ) : (
                          <Icon
                            name="arrows/arrow_drop_down"
                            width={28}
                            height={28}
                            color="var(--system-sky-600)"
                          />
                        )}
                      </button>
                    </div>
                    {!satisfactionAndCoveragePatternsCollapsed && (
                      <div className={cbStyles.emotionalBeatsGrid}>
                        {data.businessIntelligence.satisfactionAndCoveragePatterns.map((pattern, i) => (
                          <div key={i} className={`${reportStyles.textLeft} ${cbStyles.beatCard}`}>
                            <div className={cbStyles.beatStage}>{pattern?.segmentLabel || pattern?.pattern || '—'}</div>
                            {pattern?.emotionalArc && (
                              <div className={`${reportStyles.textSecondary} ${cbStyles.beatDescription}`}>
                                <strong>Emotional Arc:</strong> {formatValue(pattern.emotionalArc)}
                              </div>
                            )}
                            {pattern?.description && (
                              <div className={`${reportStyles.textSecondary} ${cbStyles.beatDescription}`}>
                                {formatValue(pattern.description)}
                              </div>
                            )}
                            {Array.isArray(pattern?.traits) && pattern.traits.length > 0 && (
                              <div className={reportStyles.mb8}>
                                <div className={reportStyles.textSecondary}>Traits</div>
                                {renderPills(pattern.traits, `pattern-traits-${i}`)}
                              </div>
                            )}
                            {Array.isArray(pattern?.coverageStrength) && pattern.coverageStrength.length > 0 && (
                              <div className={reportStyles.mb8}>
                                <div className={reportStyles.textSecondary}>Coverage Strength</div>
                                {renderPills(pattern.coverageStrength, `pattern-coverage-${i}`)}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {Array.isArray(data.businessIntelligence.coreDrivers) && data.businessIntelligence.coreDrivers.length > 0 && (
                  <div className={reportStyles.mb8}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div className={`${reportStyles.textSecondary} ${reportStyles.textLeft} ${cbStyles.sectionHeaderPad}`}>
                        Core Drivers
                      </div>
                      <button
                        type="button"
                        aria-label={coreDriversCollapsed ? 'Expand' : 'Collapse'}
                        onClick={() => setCoreDriversCollapsed(v => !v)}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 16, lineHeight: 1, display: 'flex', alignItems: 'center' }}
                      >
                        {coreDriversCollapsed ? (
                          <Icon
                            name="arrows/arrow_right"
                            width={24}
                            height={24}
                            color="var(--system-sky-600)"
                          />
                        ) : (
                          <Icon
                            name="arrows/arrow_drop_down"
                            width={24}
                            height={24}
                            color="var(--system-sky-600)"
                          />
                        )}
                      </button>
                    </div>
                    {!coreDriversCollapsed && (
                      <div className={cbStyles.emotionalBeatsGrid}>
                        {data.businessIntelligence.coreDrivers.map((d, i) => (
                          <div key={i} className={`${reportStyles.textLeft} ${cbStyles.beatCard}`}>
                            <div className={cbStyles.beatStage}>{d?.driver || '—'}</div>
                            {d?.description && (
                              <div className={`${reportStyles.textSecondary} ${cbStyles.beatDescription}`}>
                                {String(d.description)}
                              </div>
                            )}
                            {Array.isArray(d?.evidence) && d.evidence.length > 0 && (
                              <div className={reportStyles.mb8}>
                                <div className={reportStyles.textSecondary}>Evidence</div>
                                <div className={cbStyles.emotionsRow}>
                                  {d.evidence.map((ev, j) => (
                                    <span key={j} className={reportStyles.interviewQuote}>{String(ev)}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {Array.isArray(data.businessIntelligence.growthOpportunities) && data.businessIntelligence.growthOpportunities.length > 0 && (
                  <div className={reportStyles.mb8}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div className={reportStyles.textSecondary}>Growth Opportunities</div>
                      <button
                        type="button"
                        aria-label={growthOpportunitiesCollapsed ? 'Expand' : 'Collapse'}
                        onClick={() => setGrowthOpportunitiesCollapsed(v => !v)}
                        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 16, lineHeight: 1, display: 'flex', alignItems: 'center' }}
                      >
                        {growthOpportunitiesCollapsed ? (
                          <Icon
                            name="arrows/arrow_right"
                            width={28}
                            height={28}
                            color="var(--system-sky-600)"
                          />
                        ) : (
                          <Icon
                            name="arrows/arrow_drop_down"
                            width={28}
                            height={28}
                            color="var(--system-sky-600)"
                          />
                        )}
                      </button>
                    </div>
                    {!growthOpportunitiesCollapsed && (
                      <div className={cbStyles.emotionalBeatsGrid}>
                        {data.businessIntelligence.growthOpportunities.map((o, i) => (
                          <div key={i} className={`${reportStyles.textLeft} ${cbStyles.beatCard}`}>
                            <div className={cbStyles.beatStage}>{o?.opportunity || '—'}</div>
                            {o?.description && (
                              <div className={`${reportStyles.textSecondary} ${cbStyles.beatDescription}`}>
                                {String(o.description)}
                              </div>
                            )}
                            {Array.isArray(o?.expectedBenefits) && o.expectedBenefits.length > 0 && (
                              <div className={reportStyles.mb8}>
                                <div className={reportStyles.textSecondary}>Expected Benefits</div>
                                <div className={cbStyles.emotionsRow}>
                                  {o.expectedBenefits.map((b, j) => (
                                    <span key={j} className={reportStyles.pill}>{String(b)}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
          )}

          {!collapsed && Array.isArray(data.experienceGaps) && data.experienceGaps.length > 0 && (
            <div className={styles.section}>
              <div className={`${styles.sectionSubTitle} ${cbStyles.sectionMainHeader} ${cbStyles.sectionHeaderPadMd}`}>
                Experience Gaps
              </div>
              <div className={cbStyles.sectionHeaderPadSm}></div>
              <div className={cbStyles.emotionalBeatsGrid}>
                {data.experienceGaps.map((g, i) => {
                  const priorityLabel = String(g?.priority || '').toLowerCase();
                  let priorityClass = '';

                  if (priorityLabel === 'high') {
                    priorityClass = reportStyles.priorityHigh;
                  } else if (priorityLabel === 'medium') {
                    priorityClass = reportStyles.priorityMedium;
                  } else if (priorityLabel === 'low') {
                    priorityClass = reportStyles.priorityLow;
                  }

                  return (
                    <div key={i} className={`${reportStyles.textLeft} ${cbStyles.beatCard}`}>
                      <div className={cbStyles.beatStage}>
                        {g?.label || g?.gapId || '—'}
                        {g?.priority && (
                          <span className={`${reportStyles.pillStrength} ${priorityClass}`}>
                            Priority: {String(g.priority)}
                          </span>
                        )}
                      </div>
                    {g?.currentState && (
                      <div className={reportStyles.mb8}>
                        <div className={reportStyles.textSecondary}>Current State</div>
                        <div>{String(g.currentState)}</div>
                      </div>
                    )}
                    {g?.expectedState && (
                      <div className={reportStyles.mb8}>
                        <div className={reportStyles.textSecondary}>Expected State</div>
                        <div>{String(g.expectedState)}</div>
                      </div>
                    )}
                    {g?.emotionalConsequence && (
                      <div className={reportStyles.mb8}>
                        <div className={reportStyles.textSecondary}>Emotional Consequence</div>
                        <div>{String(g.emotionalConsequence)}</div>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            </div>
          )}

          {!collapsed && data.actionPlaybook && typeof data.actionPlaybook === 'object' && (
            <div className={styles.section}>
              <div className={`${styles.sectionSubTitle} ${cbStyles.sectionMainHeader} ${cbStyles.sectionHeaderPadMd} ${cbStyles.sectionHeaderPadTopMd}`}>
                Action Playbook
              </div>
              <div className={cbStyles.sectionHeaderPadSm}></div>

              {data.actionPlaybook.guidingPrinciple && (
                <div className={reportStyles.mb8}>
                  <div className={`${reportStyles.textSecondary} ${reportStyles.textLeft} ${cbStyles.sectionHeaderPadMd}`}>Guiding Principle</div>
                  <div className={reportStyles.textLeft}>{String(data.actionPlaybook.guidingPrinciple)}</div>
                </div>
              )}

              {Array.isArray(data.actionPlaybook.quickWins) && data.actionPlaybook.quickWins.length > 0 && (
                <div className={reportStyles.mb8}>
                  <div className={`${reportStyles.textSecondary} ${reportStyles.textLeft} ${cbStyles.sectionHeaderPadMd}`}>
                    Quick Wins
                  </div>
                  <div className={cbStyles.emotionalBeatsGrid}>
                    {data.actionPlaybook.quickWins.map((q, i) => {
                      const quickWinPriorityLabel = String(q?.priority || '').toLowerCase();
                      let quickWinPriorityClass = '';

                      if (quickWinPriorityLabel === 'high') {
                        quickWinPriorityClass = reportStyles.priorityHigh;
                      } else if (quickWinPriorityLabel === 'medium') {
                        quickWinPriorityClass = reportStyles.priorityMedium;
                      } else if (quickWinPriorityLabel === 'low') {
                        quickWinPriorityClass = reportStyles.priorityLow;
                      }

                      return (
                        <div key={i} className={`${reportStyles.textLeft} ${cbStyles.beatCard}`}>
                          <div className={cbStyles.beatStage}>
                            {q?.title || q?.id || '—'}
                            {q?.priority && (
                              <span className={`${reportStyles.pillStrength} ${quickWinPriorityClass}`}>
                                {String(q.priority)}
                              </span>
                            )}
                          </div>
                        {q?.description && (
                          <div className={`${reportStyles.textSecondary} ${cbStyles.beatDescription}`}>
                            {String(q.description)}
                          </div>
                        )}
                        {Array.isArray(q?.tacticalSteps) && q.tacticalSteps.length > 0 && (
                          <div className={reportStyles.mb8}>
                            <div className={`${reportStyles.textSecondary} ${reportStyles.textLeft} ${cbStyles.sectionHeaderPadMd}`}>Tactical Steps</div>
                            <div className={`${reportStyles.textLeft} ${cbStyles.emotionsRow} ${cbStyles.sectionHeaderPadSm}`}>
                              {q.tacticalSteps.map((step, j) => (
                                <span key={j} className={reportStyles.pill}>{String(step)}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {q?.targetEmotion && (
                          <div className={reportStyles.mb8}>
                            <div className={`${reportStyles.textSecondary} ${reportStyles.textLeft} ${cbStyles.sectionHeaderPadMd}`}>
                              Target Emotion
                            </div>
                            <div>{String(q.targetEmotion)}</div>
                          </div>
                        )}
                      </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {Array.isArray(data.actionPlaybook.designedExperiments) && data.actionPlaybook.designedExperiments.length > 0 && (
                <div className={reportStyles.mb8}>
                  <div className={`${reportStyles.textSecondary} ${reportStyles.textLeft} ${cbStyles.sectionHeaderPadMd} ${cbStyles.sectionHeaderPadTopMd}`}>
                    Designed Experiments
                  </div>
                  <div className={cbStyles.emotionalBeatsGrid}>
                    {data.actionPlaybook.designedExperiments.map((exp, i) => (
                      <div key={i} className={`${reportStyles.textLeft} ${cbStyles.beatCard}`}>
                        <div className={cbStyles.beatStage}>{exp?.name || exp?.experimentId || '—'}</div>
                        {exp?.hypothesis && (
                          <div className={`${reportStyles.textSecondary} ${cbStyles.beatDescription}`}>
                            {String(exp.hypothesis)}
                          </div>
                        )}
                        {exp?.design && (
                          <div className={reportStyles.mb8}>
                            <div className={`${reportStyles.textSecondary} ${reportStyles.textLeft} ${cbStyles.sectionHeaderPadMd}`}>
                              Design
                            </div>
                            {Array.isArray(exp.design.conditions) && exp.design.conditions.length > 0 && (
                              <div className={reportStyles.mb8}>
                                <div className={`${reportStyles.textSecondary} ${reportStyles.textLeft} ${cbStyles.sectionHeaderPadSm}`}>
                                  Conditions
                                </div>
                                <div className={cbStyles.emotionsRow}>
                                  {exp.design.conditions.map((c, j) => (
                                    <span key={j} className={reportStyles.pill}>{String(c)}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {exp.design.measurementWindow && (
                              <div className={reportStyles.mb8}>
                                <div className={`${reportStyles.textSecondary} ${reportStyles.textLeft} ${cbStyles.sectionHeaderPadSm}`}>
                                  Measurement Window
                                </div>
                                <div className={reportStyles.textLeft}>{String(exp.design.measurementWindow)}</div>
                              </div>
                            )}
                            {Array.isArray(exp.design.metrics) && exp.design.metrics.length > 0 && (
                              <div className={reportStyles.mb8}>
                                <div className={`${reportStyles.textSecondary} ${reportStyles.textLeft} ${cbStyles.sectionHeaderPadSm}`}>
                                  Metrics
                                </div>
                                <div className={cbStyles.emotionsRow}>
                                  {exp.design.metrics.map((m, j) => (
                                    <span key={j} className={reportStyles.pill}>{String(m)}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                            {Array.isArray(exp.design.feedbackMechanism) && exp.design.feedbackMechanism.length > 0 && (
                              <div className={reportStyles.mb8}>
                                <div className={`${reportStyles.textSecondary} ${reportStyles.textLeft} ${cbStyles.sectionHeaderPadSm}`}>
                                  Feedback Mechanism
                                </div>
                                <div className={cbStyles.emotionsRow}>
                                  {exp.design.feedbackMechanism.map((f, j) => (
                                    <span key={j} className={reportStyles.pill}>{String(f)}</span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                        {Array.isArray(exp?.successSignals) && exp.successSignals.length > 0 && (
                          <div className={reportStyles.mb8}>
                            <div className={`${reportStyles.textSecondary}`}>Success Signals</div>
                            <div className={`${reportStyles.textLeft} ${cbStyles.emotionsRow} ${cbStyles.sectionHeaderPadSm}`}>
                              {exp.successSignals.map((s, j) => (
                                <span key={j} className={reportStyles.pill}>{String(s)}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {Array.isArray(data.actionPlaybook.longerTermMoves) && data.actionPlaybook.longerTermMoves.length > 0 && (
                <div className={reportStyles.mb8}>
                  <div className={`${reportStyles.textSecondary} ${reportStyles.textLeft} ${cbStyles.sectionHeaderPadMd}`}>
                    Longer-term Moves
                  </div>
                  <div className={cbStyles.emotionalBeatsGrid}>
                    {data.actionPlaybook.longerTermMoves.map((m, i) => (
                      <div key={i} className={`${reportStyles.textLeft} ${cbStyles.beatCard}`}>
                        <div className={cbStyles.beatStage}>{m?.title || m?.id || '—'}</div>
                        {m?.description && (
                          <div className={`${reportStyles.textSecondary} ${cbStyles.beatDescription}`}>
                            {String(m.description)}
                          </div>
                        )}
                        {Array.isArray(m?.components) && m.components.length > 0 && (
                          <div className={reportStyles.mb8}>
                            <div className={reportStyles.textSecondary}>Components</div>
                            <div className={`${reportStyles.textLeft} ${cbStyles.emotionsRow} ${cbStyles.sectionHeaderPadSm}`}>
                              {m.components.map((c, j) => (
                                <span key={j} className={reportStyles.pill}>{String(c)}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {Array.isArray(m?.tactics) && m.tactics.length > 0 && (
                          <div className={reportStyles.mb8}>
                            <div className={reportStyles.textSecondary}>Tactics</div>
                            <div className={`${reportStyles.textLeft} ${cbStyles.emotionsRow} ${cbStyles.sectionHeaderPadSm}`}>
                              {m.tactics.map((t, j) => (
                                <span key={j} className={reportStyles.pill}>{String(t)}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {!collapsed && data.kpiHypotheses && typeof data.kpiHypotheses === 'object' && (
            <div className={styles.section}>
              <div className={`${styles.sectionSubTitle} ${cbStyles.sectionMainHeader} ${cbStyles.sectionHeaderPadMd}`}>
                KPI Hypotheses
              </div>
              <div className={cbStyles.sectionHeaderPadSm}></div>

              {Array.isArray(data.kpiHypotheses.primaryKPIs) && data.kpiHypotheses.primaryKPIs.length > 0 && (
                <div className={reportStyles.mb8}>
                  <div className={`${reportStyles.textSecondary} ${reportStyles.textLeft} ${cbStyles.sectionHeaderPadMd}`}>
                    Primary KPIs
                  </div>
                  <div className={cbStyles.emotionalBeatsGrid}>
                    {data.kpiHypotheses.primaryKPIs.map((k, i) => {
                      const rawDirection = String(k?.expectedDirection || '');
                      const directionLabel = rawDirection.toLowerCase();
                      let directionClass = '';
                      let directionDisplay = rawDirection;

                      if (directionLabel === 'up' || directionLabel.includes('up') || directionLabel === 'increase' || directionLabel === 'increasing') {
                        directionClass = reportStyles.priorityLow; // green
                        directionDisplay = `↑ ${rawDirection}`;
                      } else if (directionLabel === 'down' || directionLabel === 'decrease' || directionLabel === 'decreasing' || directionLabel.includes('down')) {
                        directionClass = reportStyles.priorityHigh; // red
                        directionDisplay = `↓ ${rawDirection}`;
                      }

                      return (
                        <div key={i} className={`${reportStyles.textLeft} ${cbStyles.beatCard}`}>
                          <div className={cbStyles.beatStage}>
                            {k?.name || '—'}
                            {k?.expectedDirection && (
                              <span className={`${reportStyles.pillStrength} ${directionClass}`}>
                                {directionDisplay}
                              </span>
                            )}
                          </div>
                          {k?.hypothesis && (
                            <div className={`${reportStyles.textSecondary} ${cbStyles.beatDescription}`}>
                              {String(k.hypothesis)}
                            </div>
                          )}
                          {k?.whyItMatters && (
                            <div className={reportStyles.mb8}>
                              <div className={reportStyles.textSecondary}>Why It Matters</div>
                              <div>{String(k.whyItMatters)}</div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {Array.isArray(data.kpiHypotheses.secondaryKPIs) && data.kpiHypotheses.secondaryKPIs.length > 0 && (
                <div className={reportStyles.mb8}>
                  <div className={`${reportStyles.textSecondary} ${reportStyles.textLeft} ${cbStyles.sectionHeaderPadMd}`}>
                    Secondary KPIs
                  </div>
                  <div className={cbStyles.emotionalBeatsGrid}>
                    {data.kpiHypotheses.secondaryKPIs.map((k, i) => (
                      <div key={i} className={`${reportStyles.textLeft} ${cbStyles.beatCard}`}>
                        <div className={cbStyles.beatStage}>{k?.name || '—'}</div>
                        {k?.hypothesis && (
                          <div className={`${reportStyles.textSecondary} ${cbStyles.beatDescription}`}>
                            {String(k.hypothesis)}
                          </div>
                        )}
                        {Array.isArray(k?.measurementIdeas) && k.measurementIdeas.length > 0 && (
                          <div className={reportStyles.mb8}>
                            <div className={reportStyles.textSecondary}>Measurement Ideas</div>
                            <div className={cbStyles.emotionsRow}>
                              {k.measurementIdeas.map((mi, j) => (
                                <span key={j} className={reportStyles.pill}>{String(mi)}</span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {!collapsed && data.openQuestions && typeof data.openQuestions === 'object' && (
            <div className={styles.section}>
              <div className={`${styles.sectionSubTitle} ${cbStyles.sectionMainHeader} ${cbStyles.sectionHeaderPadMd} ${cbStyles.sectionHeaderPadTopMd}`}>
                Open Questions
              </div>
              <div className={cbStyles.sectionHeaderPadSm}></div>

              {Array.isArray(data.openQuestions.forFurtherResearch) && data.openQuestions.forFurtherResearch.length > 0 && (
                <div className={reportStyles.mb8}>
                  <div className={`${reportStyles.textSecondary} ${reportStyles.textLeft} ${cbStyles.sectionHeaderPadMd}`}>
                    For Further Research
                  </div>
                  <div className={cbStyles.emotionsRow}>
                    {data.openQuestions.forFurtherResearch.map((q, i) => (
                      <span key={i} className={reportStyles.pill}>{String(q)}</span>
                    ))}
                  </div>
                </div>
              )}

              {Array.isArray(data.openQuestions.signalsToWatch) && data.openQuestions.signalsToWatch.length > 0 && (
                <div className={reportStyles.mb8}>
                  <div className={`${reportStyles.textSecondary} ${reportStyles.textLeft} ${cbStyles.sectionHeaderPadMd}`}>
                    Signals to Watch
                  </div>
                  <div className={cbStyles.emotionsRow}>
                    {data.openQuestions.signalsToWatch.map((s, i) => (
                      <span key={i} className={reportStyles.pill}>{String(s)}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
          )}
        </>
      )}
    </section>
  )
}
